import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import B2BLayout from '../../layouts/b2b'
import { Skeleton, Space } from 'antd'
import { getColumns } from './columns'
import { useNavigate, useLocation } from 'react-router-dom'
import { Sorter } from '../../interfaces'
import { objectToUrl } from '../utils'
import { currentFiltersProps, DataType, Enums, TablePaginationPosition } from './interface'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import './index.css'
import FiltersDrawer from '../../components/v2/filters-drawer'
import { getJsonSettingsByKey, getColumnOrder } from '../../components/v2/table-settings-button'
import DateFilter from './filter-date'
import { formatPhoneNumber } from '../orders/list/columns'
import FixedHeader from '../../layouts/b2b/fixed-header'
import DataTable from '../../components/data-table'
import { getFilters } from './filters'
import fetchAPI, { IHttpError } from '../../../lib/utils/fetch-api'
import { IEnum } from '../orders/list/interface'
import { FilterFastButtons } from '../deliveries/ui/filter-buttons/filter-fast-buttons'
import Result, { ResultStatusType } from 'antd/lib/result'

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

interface IError {
  code?: ResultStatusType
  message?: string
}

function parseUrlSearchParams(search: string): currentFiltersProps {
  const urlParams = new URLSearchParams(search)
  return {
    id: Number(urlParams.get('id')) || undefined,
    date: urlParams.get('date') || undefined,
    office_id: urlParams.get('office_id') || undefined,
    manager_id: urlParams.get('manager_id') || undefined,
    brand_id: urlParams.get('brand_id') || undefined,
    phone: urlParams.get('phone') || undefined,
    line_number: urlParams.get('line_number') || undefined,
  }
}

const CallsList = () => {
  const initialPageSize = (() => {
    const savedPageSize = localStorage.getItem('callsPageSize')
    const parsedPageSize = Number(savedPageSize)
    return parsedPageSize && !isNaN(parsedPageSize) && parsedPageSize <= 150 ? parsedPageSize : 50
  })()

  const navigate = useNavigate()
  const location = useLocation()

  const { sessionData } = useSelector(createSession)
  const isGoLeader = sessionData?.roles.includes(16)
  const isBrandLeader = !isGoLeader && sessionData?.roles.includes(32)

  const [data, setData] = useState<DataType[]>([])
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    manager_id: [],
    office_id: [],
    brand_id: [],
    line_number: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(location.search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({})
  const [loading, setLoading] = useState(true)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [sorter, setSorter] = useState<Sorter>({})
  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: initialPageSize,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })
  const [enums, setEnums] = useState<any>({})
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [statistics, setStatistics] = useState<{
    accepted: number
    outgoing: number
    missed: number
    incoming: number
  }>({
    accepted: 0,
    outgoing: 0,
    missed: 0,
    incoming: 0,
  })
  const [error, setError] = useState<null | IError>(null)

  const prevSelectedDateRef = useRef<string>('')
  const prevFiltersRef = useRef<any>({})
  const headerRef = useRef<HTMLDivElement | null>(null)

  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, location.pathname))

  useEffect(() => {
    const newFilters = parseUrlSearchParams(location.search)
    setCurrentFilters(newFilters)
  }, [location.search])

  const breadcrumbs = useMemo(() => {
    return [
      {
        href: '/call-log',
        title: 'Журнал звонков',
      },
    ]
  }, [])

  const getAvailableFilters = useCallback(() => {
    const enumsList = { types: ['managers', 'offices', 'brands'] }
    const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search))
    const selectedBrand = currentUrlParams?.brand
    const selectedOffice = currentUrlParams?.office
    fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`)
      .then(data => {
        const officesList = selectedBrand
          ? data?.offices
              ?.filter(i => String(i.brand_id) === String(selectedBrand))
              ?.map((i: IEnum) => ({ label: i.value, value: String(i.id) }))
          : data?.offices?.map((i: IEnum) => ({ label: i.value, value: String(i.id) }))

        const managersList =
          selectedOffice || selectedBrand
            ? data?.managers
                ?.filter(i =>
                  selectedOffice
                    ? String(i.office_id) === String(selectedOffice)
                    : String(i.brand_id) === String(selectedBrand),
                )
                ?.map((i: IEnum) => ({ label: i.value, value: String(i.id) }))
            : data?.managers?.map((i: IEnum) => ({ label: i.value, value: String(i.id) }))

        setEnums(data)

        const filters = {
          manager_id: managersList,
          office_id: officesList,
          brand_id: data?.brands?.map((i: IEnum) => ({ label: i.value, value: String(i.id) })),
        }

        setAvailableFilters(filters)
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
      })
  }, [])

  const getData = useCallback(() => {
    setLoading(true)
    const urlParamsObj: any = Object.fromEntries(new URLSearchParams(location.search))

    if (!urlParamsObj.pageSize) {
      urlParamsObj.pageSize = initialPageSize
    }

    const { page, pageSize, ...currentFilters } = urlParamsObj

    fetchAPI(`/api/call-center${objectToUrl(urlParamsObj)}`)
      .then(async ({ data, pagination, sorter, statistics, available_filters }) => {
        if (data) {
          setData(
            data.map((item: any) => ({
              ...item,
            })),
          )
          setPagination(prevState => ({
            ...prevState,
            ...pagination,
          }))
          setSorter(sorter)
          setStatistics(statistics || { accepted: 0, outgoing: 0, missed: 0, incoming: 0 })
          if (available_filters?.line_number) {
            setAvailableFilters(filters => ({
              ...filters,
              line_number:
                available_filters?.line_number?.map(i => ({
                  label: formatPhoneNumber(i),
                  value: i,
                })) || [],
            }))
          }
        }
        setLoading(false)

        prevFiltersRef.current = currentFilters
        prevSelectedDateRef.current = selectedDate
      })
      .catch(error => {
        setError({
          message: (error as IHttpError)?.message || 'Неизвестная ошибка',
          code: ((error as IHttpError)?.statusCode || 'error') as ResultStatusType,
        })
      })
      .finally(() => setLoading(false))
  }, [location.search])

  useEffect(() => {
    getAvailableFilters()
  }, [])

  useEffect(() => {
    getData()
  }, [location.search])

  useEffect(() => {
    let newSelectedDate = ''
    if (currentFilters?.date) {
      const [startDate, endDate] = currentFilters.date.split('-')
      newSelectedDate = startDate === endDate ? startDate : ''
    }

    if (newSelectedDate && newSelectedDate !== prevSelectedDateRef.current) {
      setSelectedDate(newSelectedDate)
    } else if (!newSelectedDate) {
      setSelectedDate('')
    }
  }, [currentFilters])

  const changeFiltersHandler = useCallback(
    (filters: any) => {
      let tmpFilter = { ...(filters || {}), pageSize: pagination.pageSize || 100 }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()

      const dates = filters?.delivery_date?.split('-') || []
      const newSelectedDate = dates[0] === dates[1] ? dates[0] : ''

      if (newSelectedDate) {
        setSelectedDate(newSelectedDate)
      } else if (!newSelectedDate) {
        setSelectedDate('')
      }

      navigate({ search: newUrlParams })
    },
    [pagination.pageSize, history],
  )

  const columns = useMemo(() => getColumns(isGoLeader, isBrandLeader, sorter), [
    isBrandLeader,
    isBrandLeader,
    sorter,
  ])

  const viewColumns = useMemo(() => {
    const enhanceColumn = (column: any) => {
      if (!('dataIndex' in column) || !column.editable) {
        return column
      }

      return {
        ...column,
      }
    }

    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const enhancedColumns = columns.map(enhanceColumn)

      let result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = enhancedColumns.find(column => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }
      return result
    }

    return columns.map(enhanceColumn)
  }, [columns, jsonSettings, enums])

  const changeDateFilterHandler = (newDate: string) => {
    setLoading(true)
    setData([])
    const urlParams = new URLSearchParams(location.search)

    if (newDate) {
      urlParams.set('date', `${newDate}-${newDate}`)
    } else {
      urlParams.delete('date')
    }

    setSelectedDate(newDate)

    urlParams.set('page', '1')

    setPagination(prevState => ({
      ...prevState,
      current: 1,
    }))

    navigate({ search: urlParams.toString() })
  }

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))

    if (!isNaN(pageSize)) {
      localStorage.setItem('callsPageSize', String(pageSize))
    }

    const currentUrlParams = new URLSearchParams(location.search)
    if (page && page !== 1) {
      currentUrlParams.set('page', page.toString())
    } else {
      currentUrlParams.delete('page')
    }
    if (!isNaN(pageSize)) {
      if (pageSize && pageSize !== 50) {
        currentUrlParams.set('pageSize', pageSize.toString())
      } else {
        currentUrlParams.delete('pageSize')
      }
    }
    navigate({ search: currentUrlParams.toString() })
  }

  const statsFilters = useMemo(
    () => [
      {
        name: `Принятых: <b>${statistics.accepted}</b>`,
        code: 'accepted',
      },
      {
        name: `<span style="color: #dc0000">Пропущенных: <b>${statistics.missed}</b></span>`,
        code: 'missed',
      },
      {
        name: `Исходящих: <b>${statistics.outgoing}</b>`,
        code: 'outgoing',
      },
      {
        name: `Входящих: <b>${statistics.incoming}</b>`,
        code: 'incoming',
      },
    ],
    [statistics],
  )

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton>
      {loading && <Skeleton active />}
      {!loading && error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!loading && !error && (
        <>
          <FixedHeader ref={headerRef}>
            <Space
              style={{ marginBottom: '12px', width: '100%', justifyContent: 'space-between' }}
              wrap
            >
              <Space>
                <DateFilter selectedDate={selectedDate} onChange={changeDateFilterHandler} />
                <FilterFastButtons
                  groupName="type"
                  filters={statsFilters}
                  onChange={changeFiltersHandler}
                  isSingleSelect
                />
              </Space>
              <FiltersDrawer
                filters={getFilters({
                  currentFilters,
                  availableFilters,
                  isGoLeader,
                  isBrandLeader,
                })}
                onChange={changeFiltersHandler}
                currentFilters={currentFilters}
              />
            </Space>
          </FixedHeader>
          <DataTable
            columns={viewColumns}
            data={data}
            loading={loading}
            rowKey={r => String(r.id)}
            pagination={pagination}
            onPageChange={handlePageChange}
            className={'call-table'}
            headerRef={headerRef}
            maxLines={1}
            bordered
          />
        </>
      )}
    </B2BLayout>
  )
}

export default CallsList
